/* ══════════════════════════════════════════════════════════════
   src/content/domains/data.tsx
   All static content for the Domains page — overview metadata,
   colors, class–domain mapping, card anatomy items, and vault rules.
   ══════════════════════════════════════════════════════════════ */

import { ReactNode } from 'react';
import { LOADOUT, VAULT, RECALL_COST, STRESS } from '../templates';

/* ── Domain overview cards ── */
export interface DomainEntry {
  name: string;
  cssClass: string;
  classes: string[];
  description: ReactNode;
  themes: string[];
}

export const domains: DomainEntry[] = [
  {
    name: 'Arcana', cssClass: 'arcana',
    classes: ['Druid', 'Sorcerer'],
    description: <p>Arcana is the domain of <strong>innate and instinctual magic</strong>. Those
      who choose this path tap into the raw, enigmatic forces of the
      realms to manipulate both their own energy and the elements.
      Arcana offers wielders a volatile power, but it is incredibly
      potent when correctly channeled.</p>,
    themes: ['Innate magic', 'Elemental forces', 'Volatile power', 'Instinct'],
  },
  {
    name: 'Blade', cssClass: 'blade',
    classes: ['Assassin', 'Blood Hunter', 'Guardian', 'Warrior'],
    description: <p>Blade is the domain of <strong>weapon mastery</strong>. Whether by steel,
      bow, or perhaps a more specialized arm, those who follow this
      path have the skill to cut short the lives of others. Wielders of
      Blade dedicate themselves to achieving inexorable power over
      death.</p>,
    themes: ['Weapon mastery', 'Combat power', 'Martial skill', 'Deadly strikes'],
  },
  {
    name: 'Bone', cssClass: 'bone',
    classes: ['Brawler', 'Ranger', 'Warrior'],
    description: <p>Bone is the domain of <strong>tactics and the body</strong>. Practitioners
      of this domain have an uncanny control over their own
      physical abilities and an eye for predicting the behaviors of
      others in combat. Adherents to Bone gain an unparalleled
      understanding of bodies and their movements.</p>,
    themes: ['Tactics', 'Body control', 'Combat prediction', 'Physical mastery'],
  },
  {
    name: 'Codex', cssClass: 'codex',
    classes: ['Bard', 'Wizard'],
    description: <p>Codex is the domain of <strong>intensive magical study</strong>. Those who
      seek magical knowledge turn to the equations of power
      recorded in books, written on scrolls, etched into walls, or
      tattooed on bodies. Codex offers a commanding and versatile
      understanding of magic to devotees who pursue knowledge
      beyond the boundaries of common wisdom. </p>,
    themes: ['Magical study', 'Lore', 'Scrolls & tomes', 'Learned power'],
  },
  {
    name: 'Grace', cssClass: 'grace',
    classes: ['Bard', 'Rogue', 'Warlock'],
    description: <p>Grace is the domain of <strong>charisma</strong>. Through rapturous
      storytelling, charming spells, or a shroud of lies, those who
      channel this power define the realities of their adversaries,
      bending perception to their will. Grace offers its wielders raw
      magnetism and mastery over language.</p>,
    themes: ['Charisma', 'Persuasion', 'Deception', 'Social mastery'],
  },
  {
    name: 'Midnight', cssClass: 'midnight',
    classes: ['Assassin', 'Rogue', 'Sorcerer'],
    description: <p>Midnight is the domain of <strong>shadows and secrecy</strong>. Whether
      by clever tricks, deft magic, or the cloak of night, those who
      channel these forces practice the art of obscurity and can
      uncover sequestered treasures. Midnight offers practitioners
      the power to control and create enigmas. </p>,
    themes: ['Shadow magic', 'Secrecy', 'Obscurity', 'Hidden truths'],
  },
  {
    name: 'Sage', cssClass: 'sage',
    classes: ['Druid', 'Ranger'],
    description: <p>Sage is the domain <strong>of the natural world</strong>. Those who walk
      this path tap into the unfettered power of the earth and its
      creatures to unleash raw magic. Sage grants its adherents
      the vitality of a blooming flower and the ferocity of a ravenous
      predator.</p>,
    themes: ['Nature magic', 'Beasts', 'Primal forces', 'The wild'],
  },
  {
    name: 'Splendor', cssClass: 'splendor',
    classes: ['Seraph', 'Wizard'],
    description: <p>Splendor is the domain of <strong>life</strong>. Through this magic, followers
      gain the ability to heal and, to an extent, control death.
      Splendor offers its disciples the magnificent ability to both
      give and end life.</p>,
    themes: ['Healing', 'Life magic', 'Restoration', 'Divine light'],
  },
  {
    name: 'Valor', cssClass: 'valor',
    classes: ['Brawler', 'Guardian', 'Seraph'],
    description: <p>Valor is the domain of <strong>protection</strong>. Whether through attack or
      defense, those who choose this discipline channel formidable
      strength to protect their allies in battle. Valor offers great
      power to those who raise their shields in defense of others. </p>,
    themes: ['Protection', 'Defense', 'Shield', 'Ally support'],
  },
  {
    name: 'Blood', cssClass: 'blood',
    classes: ['Blood Hunter'],
    description: <p>Blood is the domain of <strong>vital force and crimson power</strong>. Those
      who walk this path tap into the primal energy of blood itself,
      wielding it as both weapon and shield. Blood hunters channel
      pain into power and life into destruction.</p>,
    themes: ['Blood magic', 'Pain as power', 'Vital force', 'Hunter\'s instinct'],
  },
  {
    name: 'Dread', cssClass: 'dread',
    classes: ['Warlock'],
    description: <p>Dread is the domain of <strong>fear and consuming darkness</strong>. Those
      who walk this path wield terror as a weapon, twisting the minds
      of their enemies and summoning horrors from the void. Dread
      practitioners thrive in the suffering of others.</p>,
    themes: ['Fear', 'Darkness', 'Spectral horror', 'Terror'],
  },
];

/* ── Domain accent colors ── */
export const DOMAIN_COLORS: Record<string, string> = {
  Arcana: '#c47fff', Blade: '#ff6e6e', Bone: '#c0c0c0',
  Codex: '#6aaeff', Grace: '#f06ab0', Midnight: '#8860c0',
  Sage: '#60d890', Splendor: '#e8c547', Valor: '#f0903a',
  Blood: '#8b0000',
  Dread: '#7c3aed',
};

/* ── Class → domain mapping ── */
export const CLASS_DOMAINS: Record<string, string[]> = {
  Assassin: ['Blade', 'Midnight'],
  Bard: ['Codex', 'Grace'],
  Brawler: ['Bone', 'Valor'],
  'Blood Hunter': ['Blade', 'Blood'],
  Druid: ['Arcana', 'Sage'],
  Guardian: ['Blade', 'Valor'],
  Ranger: ['Bone', 'Sage'],
  Rogue: ['Grace', 'Midnight'],
  Seraph: ['Splendor', 'Valor'],
  Sorcerer: ['Arcana', 'Midnight'],
  Warlock: ['Dread', 'Grace'],
  Warrior: ['Blade', 'Bone'],
  Wizard: ['Codex', 'Splendor'],
};

/* ── Filter option lists ── */
export const ALL_DOMAINS = ['All', 'Arcana', 'Blade', 'Blood', 'Bone', 'Codex', 'Dread', 'Grace', 'Midnight', 'Sage', 'Splendor', 'Valor'];
export const ALL_TYPES = ['All', 'Ability', 'Spell', 'Grimoire'];
export const ALL_LEVELS = ['All', ...Array.from({ length: 10 }, (_, i) => String(i + 1))];
export const ALL_CLASSES = ['All', ...Object.keys(CLASS_DOMAINS)];

/* ── Card Anatomy items ── */
export interface AnatomyItem {
  label: string;
  text: ReactNode;
}

export const cardAnatomy: AnatomyItem[] = [
  {
    label: 'Level',
    text: <p>The number in the top left of the card indicates the card’s
      level. You cannot acquire a domain card with a level higher
      than your PC’s.</p>,
  },
  {
    label: 'Domain',
    text: <p>Beneath the card’s level there is a symbol indicating its
      domain. You can only choose cards from your class’s two
      domains. </p>,
  },
  {
    label: 'Recall Cost',
    text: <p>The number in the top right of the card. This is the {STRESS} you mark to swap this card from your {VAULT} into your {LOADOUT} during play. Swapping during downtime is free.</p>,
  },
  {
    label: 'Title',
    text: <p>The name of the card.</p>,
  },
  {
    label: 'Type',
    text: <p>The card’s <strong>type</strong> is listed in the center above the title. There
      are three types of domain cards: <strong>abilities</strong>, <strong>spells</strong>, and{' '}
      <strong>grimoires</strong>. Abilities are typically non-magical in nature,
      while spells are magical. Grimoires are unique to the Codex
      domain and grant access to a collection of less potent
      spells. Some game mechanics only apply to certain types
      of cards.</p>,
  },
  {
    label: 'Feature',
    text: <p>The text on the bottom half of the card describes its
      feature(s), including any special rules you need to follow
      when you use that card.</p>,
  },
];

/* ── Loadout & Vault rules ── */
export const vaultRules: ReactNode[] = [
  <p>Your <strong>loadout</strong> is the set of acquired domain cards whose effects your PC can use during play. You can have up to 5 domain cards in your loadout at one time. Once you've acquired six or more domain cards, you must choose five to keep in your {LOADOUT}; the rest are considered to be in your <strong>vault</strong>. Vault cards are inactive and do not influence play.</p>,
  <blockquote><strong>Note:</strong> <em>Your subclass, ancestry, and community cards don't count toward your loadout or vault and are always active and available.</em></blockquote>,
  <p>At the start of a rest, before using downtime moves, you can freely move cards between your loadout and your vault, so long as your loadout doesn't exceed its five-card maximum.</p>,
  <p>To move a card from your {VAULT} to your {LOADOUT} at any other time, you must mark a number of {STRESS} equal to the vaulted card's {RECALL_COST} (located in the top right of the card next to the lightning bolt symbol). If your loadout is already full, you must also move a card from your loadout to your vault to make space, though you can do this at no cost.</p>,
  <p>When you gain a new domain card at level-up, you can immediately move it into your loadout for free. If your loadout is already full, you must also move a card from your loadout to your vault to make space.</p>,
];
